/** @velyn-custom BEGIN — vely-ugc-testimonials.js (ordered flex columns + show more/less + viewport video) */
(function () {
  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  function bindReducedMotionVideos(root) {
    if (!REDUCED_MOTION.matches) return;

    root.querySelectorAll("video-local-scroll").forEach((host) => {
      const bindVideo = () => {
        const video = host.querySelector("video");
        if (!video || video.dataset.ugcReducedMotionBound === "true") return;
        video.dataset.ugcReducedMotionBound = "true";
        video.addEventListener("play", () => {
          if (REDUCED_MOTION.matches) video.pause();
        });
      };

      bindVideo();
      const observer = new MutationObserver(bindVideo);
      observer.observe(host, { childList: true, subtree: true });
      host._ugcReducedMotionObserver = observer;
    });
  }

  function unbindReducedMotionVideos(root) {
    root.querySelectorAll("video-local-scroll").forEach((host) => {
      host._ugcReducedMotionObserver?.disconnect();
      delete host._ugcReducedMotionObserver;
      host.querySelectorAll("video").forEach((video) => {
        delete video.dataset.ugcReducedMotionBound;
      });
    });
  }

  function isCardVisibleInUgcWrap(card, wrap) {
    if (!card || !wrap) return true;
    const cardRect = card.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    return cardRect.bottom > wrapRect.top + 1 && cardRect.top < wrapRect.bottom - 1;
  }

  function lockNativePlaybackSpeed(video) {
    if (!video || video.tagName !== "VIDEO") return;
    video.defaultPlaybackRate = 1;
    if (video.playbackRate !== 1) video.playbackRate = 1;
    if (video.dataset.ugcRateLocked === "true") return;
    video.dataset.ugcRateLocked = "true";
    const enforce = () => {
      if (video.playbackRate !== 1) video.playbackRate = 1;
    };
    video.addEventListener("ratechange", enforce);
    video.addEventListener("play", enforce);
    video.addEventListener("loadedmetadata", enforce);
  }

  function manualMountUgcVideo(host) {
    if (host.getAttribute("loaded") === "true") return;
    const tpl = host.querySelector("template");
    const videoNode = tpl?.content?.firstElementChild?.cloneNode(true);
    if (!videoNode) return;

    host.appendChild(videoNode);
    host.querySelector(".video-thumbnail")?.remove();
    host.setAttribute("loaded", "true");

    const video = host.querySelector("video");
    if (!video) return;

    /* Playback only: muted/loop/playsInline for autoplay policy — do not alter speed or timeline */
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "metadata";
    lockNativePlaybackSpeed(video);
  }

  function mountUgcVideoHost(host, wrap) {
    if (host.getAttribute("loaded") === "true") {
      const existing = host.querySelector("video");
      if (existing) lockNativePlaybackSpeed(existing);
      return;
    }

    const card = host.closest("[data-vely-ugc-card]");
    const inDesignMode = Boolean(window.Shopify?.designMode);
    const visibleInWrap = isCardVisibleInUgcWrap(card, wrap);
    const rect = host.getBoundingClientRect();
    const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;

    if (!inDesignMode && !visibleInWrap && !inViewport) return;

    if (typeof host.loadContentVideo === "function") {
      host.loadContentVideo(host);
      const video = host.querySelector("video");
      if (video) {
        video.preload = video.preload === "none" ? "metadata" : video.preload || "metadata";
        lockNativePlaybackSpeed(video);
      }
      return;
    }

    manualMountUgcVideo(host);
  }

  function mountUgcVideoHosts(root) {
    const wrap = root.querySelector(".vely-ugc__grid-wrap");
    root.querySelectorAll("video-local-scroll").forEach((host) => {
      mountUgcVideoHost(host, wrap);
    });
  }

  function bindUgcVideoLazyMount(root) {
    if (root._ugcVideoMountBound) return;
    root._ugcVideoMountBound = true;

    let timer;
    const scheduleMount = () => {
      clearTimeout(timer);
      timer = setTimeout(() => mountUgcVideoHosts(root), 80);
    };

    window.addEventListener("scroll", scheduleMount, { passive: true });
    window.addEventListener("resize", scheduleMount, { passive: true });
    root.querySelector(".vely-ugc__more")?.addEventListener("click", () => {
      setTimeout(scheduleMount, UGC_HEIGHT_MS + 60);
    });
  }

  function getColumnCount(grid) {
    const desktop = Number(grid.dataset.columnsDesktop) || 4;
    const tablet = Number(grid.dataset.columnsTablet) || 2;
    const mobile = Number(grid.dataset.columnsMobile) || 1;
    const w = window.innerWidth;
    if (w >= 1025) return desktop;
    if (w >= 768) return tablet;
    return mobile;
  }

  function getCardsInBlockOrder(grid) {
    const cards = Array.from(grid.querySelectorAll("[data-vely-ugc-card]"));
    return cards.sort((a, b) => {
      const orderA = Number(a.dataset.ugcOrder);
      const orderB = Number(b.dataset.ugcOrder);
      if (Number.isFinite(orderA) && Number.isFinite(orderB)) return orderA - orderB;
      return 0;
    });
  }

  function layoutColumns(grid) {
    const cards = getCardsInBlockOrder(grid);
    const cols = getColumnCount(grid);
    const section = grid.closest(".vely-ugc");

    grid.innerHTML = "";
    const columns = Array.from({ length: cols }, () => {
      const col = document.createElement("div");
      col.className = "vely-ugc__col";
      col.setAttribute("role", "list");
      grid.appendChild(col);
      return col;
    });

    if (!cards.length) {
      grid.classList.add("vely-ugc__grid--ready");
      if (section) section.classList.add("vely-ugc--ready");
      return;
    }

    // Manual column (data-ugc-column) when set; otherwise round-robin.
    // Within each column, preserve Theme Editor block order.
    const buckets = Array.from({ length: cols }, () => []);
    let autoIndex = 0;

    cards.forEach((card) => {
      const assigned = Number(card.dataset.ugcColumn);
      let colIndex;
      if (Number.isFinite(assigned) && assigned >= 1) {
        colIndex = (assigned - 1) % cols;
      } else {
        colIndex = autoIndex % cols;
        autoIndex += 1;
      }
      buckets[colIndex].push(card);
    });

    buckets.forEach((list, i) => {
      list.forEach((card) => columns[i].appendChild(card));
    });

    grid.classList.add("vely-ugc__grid--ready");
    if (section) section.classList.add("vely-ugc--ready");
    if (section) {
      mountUgcVideoHosts(section);
    }
  }

  const UGC_HEIGHT_MS = 450;

  function getCollapsedMax(wrap) {
    const raw = getComputedStyle(wrap).getPropertyValue("--vely-ugc-collapsed-max").trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 849;
  }

  function getUgcScrollTargetY(root) {
    const section = root.closest(".section-vely-ugc-testimonials");
    const anchor = root.querySelector(".vely-ugc__more") || section || root;
    const offset = 16;
    return Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - offset);
  }

  function finishWrapAnimation(wrap) {
    wrap.classList.remove("vely-ugc__grid-wrap--animating");
    wrap.style.height = "auto";
  }

  function watchHeightTransition(wrap, done) {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      wrap.removeEventListener("transitionend", onEnd);
      clearTimeout(fallback);
      done();
    };
    const onEnd = (event) => {
      if (event.target !== wrap || event.propertyName !== "height") return;
      finish();
    };
    wrap.addEventListener("transitionend", onEnd);
    const fallback = setTimeout(finish, UGC_HEIGHT_MS + 80);
  }

  function expandWrap(wrap) {
    if (wrap.dataset.ugcAnimating === "true") return;
    wrap.dataset.ugcAnimating = "true";

    const startHeight = wrap.getBoundingClientRect().height;
    wrap.style.height = `${startHeight}px`;
    wrap.classList.add("vely-ugc__grid-wrap--animating");
    wrap.removeAttribute("data-collapsed");
    void wrap.offsetHeight;

    const endHeight = wrap.scrollHeight;

    watchHeightTransition(wrap, () => {
      finishWrapAnimation(wrap);
      wrap.dataset.ugcAnimating = "";
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        wrap.style.height = `${endHeight}px`;
      });
    });
  }

  function collapseWrap(wrap, root) {
    if (wrap.dataset.ugcAnimating === "true") return;
    wrap.dataset.ugcAnimating = "true";

    const btn = root.querySelector(".vely-ugc__more");
    const anchor = btn || root;
    const anchorViewportTop = anchor.getBoundingClientRect().top;

    const startHeight = wrap.getBoundingClientRect().height;
    const endHeight = getCollapsedMax(wrap);

    wrap.style.height = `${startHeight}px`;
    wrap.classList.add("vely-ugc__grid-wrap--animating");
    void wrap.offsetHeight;

    let scrollRafId = 0;
    let scrollActive = true;

    const syncScrollToAnchor = () => {
      if (!scrollActive) return;
      const drift = anchor.getBoundingClientRect().top - anchorViewportTop;
      if (Math.abs(drift) > 0.5) {
        window.scrollBy(0, drift);
      }
      scrollRafId = requestAnimationFrame(syncScrollToAnchor);
    };

    scrollRafId = requestAnimationFrame(syncScrollToAnchor);

    watchHeightTransition(wrap, () => {
      scrollActive = false;
      cancelAnimationFrame(scrollRafId);

      const drift = anchor.getBoundingClientRect().top - anchorViewportTop;
      if (Math.abs(drift) > 0.5) {
        window.scrollBy(0, drift);
      }

      wrap.setAttribute("data-collapsed", "");
      finishWrapAnimation(wrap);

      const finalDrift = anchor.getBoundingClientRect().top - anchorViewportTop;
      if (Math.abs(finalDrift) > 0.5) {
        window.scrollBy(0, finalDrift);
      }

      wrap.dataset.ugcAnimating = "";
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        wrap.style.height = `${endHeight}px`;
      });
    });
  }

  function bindToggle(root) {
    /* Show-more HTML is omitted when section setting enable_show_more is off */
    if (!root.hasAttribute("data-ugc-collapsible")) return;

    const btn = root.querySelector(".vely-ugc__more");
    const wrap = root.querySelector(".vely-ugc__grid-wrap");
    if (!btn || !wrap || btn.dataset.ugcBound === "true") return;

    btn.dataset.ugcBound = "true";
    const labelMore = btn.dataset.labelMore || "";
    const labelLess = btn.dataset.labelLess || "";
    const labelEl = btn.querySelector(".vely-ugc__more-label");

    btn.addEventListener("click", () => {
      if (wrap.dataset.ugcAnimating === "true") return;

      const collapsed = wrap.hasAttribute("data-collapsed");
      const reducedMotion = REDUCED_MOTION.matches;

      if (collapsed) {
        if (reducedMotion) {
          wrap.removeAttribute("data-collapsed");
          wrap.style.height = "auto";
        } else {
          expandWrap(wrap);
        }
        btn.setAttribute("aria-expanded", "true");
        btn.classList.add("is-expanded");
        if (labelEl) labelEl.textContent = labelLess;
      } else {
        if (reducedMotion) {
          wrap.setAttribute("data-collapsed", "");
          wrap.style.height = "";
          window.scrollTo(0, getUgcScrollTargetY(root));
        } else {
          collapseWrap(wrap, root);
        }
        btn.setAttribute("aria-expanded", "false");
        btn.classList.remove("is-expanded");
        if (labelEl) labelEl.textContent = labelMore;
      }

      btn.focus({ preventScroll: true });
    });
  }

  function initRoot(root) {
    const grid = root.querySelector("[data-ugc-grid]");
    if (!grid || grid.dataset.ugcLayoutBound === "true") return;

    grid.dataset.ugcLayoutBound = "true";
    root._ugcLastCols = getColumnCount(grid);
    layoutColumns(grid);

    if (!root._ugcResizeBound) {
      root._ugcResizeBound = true;
      let resizeTimer;
      root._ugcOnResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const nextCols = getColumnCount(grid);
          if (nextCols === root._ugcLastCols) return;
          root._ugcLastCols = nextCols;
          layoutColumns(grid);
        }, 150);
      };
      window.addEventListener("resize", root._ugcOnResize, { passive: true });
    }

    bindToggle(root);
    bindUgcVideoLazyMount(root);
    mountUgcVideoHosts(root);
    unbindReducedMotionVideos(root);
    bindReducedMotionVideos(root);
  }

  function init(scope) {
    (scope || document).querySelectorAll(".vely-ugc").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const roots = event.target?.querySelectorAll?.(".vely-ugc") || [];
    roots.forEach((root) => {
      root.classList.remove("vely-ugc--ready");
      const btn = root.querySelector(".vely-ugc__more");
      if (btn) btn.dataset.ugcBound = "";
      const grid = root.querySelector("[data-ugc-grid]");
      if (grid) {
        grid.classList.remove("vely-ugc__grid--ready");
        grid.dataset.ugcLayoutBound = "";
        root._ugcVideoMountBound = "";
        const cards = Array.from(grid.querySelectorAll("[data-vely-ugc-card]"));
        cards.forEach((card) => grid.appendChild(card));
      }
      initRoot(root);
    });
  });
})();
/** @velyn-custom END — vely-ugc-testimonials.js */
